import React, { FormEvent, useMemo, useRef, useState } from "react";
import { BeatLoader } from "react-spinners";
import { toast } from "react-toastify";
import "../assets/css/bootstrap/bootstrap.css";
import "../assets/css/bootstrap/bootstrap-grid.css";
import "../assets/css/bootstrap/bootstrap-reboot.css";
import "../assets/css/backend-plugin.min.css";
import "../assets/vendor/remixicon/fonts/remixicon.css";
import "../assets/vendor/line-awesome/dist/line-awesome/css/line-awesome.min.css";
import "../assets/vendor/@fortawesome/fontawesome-free/css/all.min.css";
import {
  IoAlertCircleOutline,
  IoBusinessOutline,
  IoCarSportOutline,
  IoCloudUploadOutline,
  IoDocumentTextOutline,
  IoFlagOutline,
  IoInformationCircleOutline,
  IoPeopleOutline,
} from "react-icons/io5";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

type CompanyType = "" | "OEM" | "Supplier" | "Expert";
type CompanyProfileType =
  | ""
  | "fabricant_pieces_rechange"
  | "pme"
  | "startup"
  | "multinational"
  | "constructeur_oem";
type YesNo = "" | "Oui" | "Non" | "Non renseigne";
type HomologationAnswer = YesNo | "En cours";
type AlertType = "success" | "danger";

type OemContact = {
  constructorName: string;
  contactName: string;
  phone: string;
  duration: string;
  status: string;
};

type AdhererFormState = {
  companyName: string;
  companyType: CompanyType;
  companyProfileType: CompanyProfileType;
  siteName: string;
  wilaya: string;
  factoryAddress: string;
  products: string;
  productsCount: string;
  productHomologated: HomologationAnswer;
  factoryCapacity: string;
  contactName: string;
  phone: string;
  email: string;
  marketSegments: string[];
  certifications: string[];
  contactedByOem: YesNo;
  oemContacts: OemContact[];
  blockers: string;
  supportNeeds: string;
};

const initialOemContact: OemContact = {
  constructorName: "",
  contactName: "",
  phone: "",
  duration: "",
  status: "",
};

const initialFormState: AdhererFormState = {
  companyName: "",
  companyType: "",
  companyProfileType: "",
  siteName: "",
  wilaya: "",
  factoryAddress: "",
  products: "",
  productsCount: "0",
  productHomologated: "Non renseigne",
  factoryCapacity: "0",
  contactName: "Responsable non renseigne",
  phone: "",
  email: "",
  marketSegments: [],
  certifications: [],
  contactedByOem: "Non renseigne",
  oemContacts: [{ ...initialOemContact }, { ...initialOemContact }, { ...initialOemContact }],
  blockers: "",
  supportNeeds: "",
};

const wilayas = [
  "01 - Adrar",
  "02 - Chlef",
  "03 - Laghouat",
  "04 - Oum El Bouaghi",
  "05 - Batna",
  "06 - Bejaia",
  "07 - Biskra",
  "08 - Bechar",
  "09 - Blida",
  "10 - Bouira",
  "11 - Tamanrasset",
  "12 - Tebessa",
  "13 - Tlemcen",
  "14 - Tiaret",
  "15 - Tizi Ouzou",
  "16 - Alger",
  "17 - Djelfa",
  "18 - Jijel",
  "19 - Setif",
  "20 - Saida",
  "21 - Skikda",
  "22 - Sidi Bel Abbes",
  "23 - Annaba",
  "24 - Guelma",
  "25 - Constantine",
  "26 - Medea",
  "27 - Mostaganem",
  "28 - M'Sila",
  "29 - Mascara",
  "30 - Ouargla",
  "31 - Oran",
  "32 - El Bayadh",
  "33 - Illizi",
  "34 - Bordj Bou Arreridj",
  "35 - Boumerdes",
  "36 - El Tarf",
  "37 - Tindouf",
  "38 - Tissemsilt",
  "39 - El Oued",
  "40 - Khenchela",
  "41 - Souk Ahras",
  "42 - Tipaza",
  "43 - Mila",
  "44 - Ain Defla",
  "45 - Naama",
  "46 - Ain Temouchent",
  "47 - Ghardaia",
  "48 - Relizane",
  "49 - Timimoun",
  "50 - Bordj Badji Mokhtar",
  "51 - Ouled Djellal",
  "52 - Beni Abbes",
  "53 - In Salah",
  "54 - In Guezzam",
  "55 - Touggourt",
  "56 - Djanet",
  "57 - El M'Ghair",
  "58 - El Meniaa",
];

const certificationOptions = ["ISO 9001", "IATF 16949", "En cours"];
const companyProfileTypeOptions: Array<{ value: CompanyProfileType; label: string }> = [
  { value: "fabricant_pieces_rechange", label: "Fabricant pieces de rechange" },
  { value: "pme", label: "PME" },
  { value: "startup", label: "Startup" },
  { value: "multinational", label: "Multinational" },
  { value: "constructeur_oem", label: "Constructeur OEM" },
];

const getCompanyProfileTypeLabel = (value: CompanyProfileType | string) =>
  companyProfileTypeOptions.find((option) => option.value === value)?.label ||
  value ||
  "A definir";

const steps = [
  "Identification",
  "Certifications",
  "Relation OEM",
  "Blocages",
  "Validation",
];

const splitItems = (value: string) =>
  value
    .split(/[\n,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const fieldIsFilled = (value: unknown) => {
  if (Array.isArray(value)) return value.length > 0;
  return typeof value === "string" ? value.trim().length > 0 : Boolean(value);
};

const inputClass = (errors: Record<string, string>, field: string) =>
  `form-control${errors[field] ? " is-invalid" : ""}`;

const selectClass = (errors: Record<string, string>, field: string) =>
  `form-select${errors[field] ? " is-invalid" : ""}`;

const getAdhesionSubmitError = (status: number) => {
  if (status === 409) {
    return "Nous ne pouvons pas enregistrer cette demande avec les informations fournies. Verifiez les coordonnees saisies ou contactez AIDA.";
  }

  return "Votre demande n'a pas pu etre enregistree pour le moment. Veuillez reessayer plus tard ou contacter AIDA.";
};

const FieldError = ({ errors, field }: { errors: Record<string, string>; field: string }) =>
  errors[field] ? <div className="invalid-feedback">{errors[field]}</div> : null;

const Adherer: React.FC = () => {
  const [form, setForm] = useState<AdhererFormState>(initialFormState);
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<AlertType>("success");
  const [loading, setLoading] = useState(false);
  const alertRef = useRef<HTMLDivElement | null>(null);

  const productCount = useMemo(() => {
    if (form.productsCount.trim()) {
      const manualCount = Number(form.productsCount);
      if (Number.isFinite(manualCount) && manualCount >= 0) return manualCount;
    }
    return splitItems(form.products).length;
  }, [form.products, form.productsCount]);

  const completedFields = useMemo(() => {
    const fields: Array<keyof AdhererFormState> = [
      "companyName",
      "companyType",
      "companyProfileType",
      "siteName",
      "wilaya",
      "factoryAddress",
      "products",
      "productsCount",
      "productHomologated",
      "factoryCapacity",
      "contactName",
      "phone",
      "email",
      "certifications",
      "contactedByOem",
      "blockers",
      "supportNeeds",
    ];

    const filled = fields.filter((field) => fieldIsFilled(form[field])).length;
    return Math.round((filled / fields.length) * 100);
  }, [form]);

  const updateForm = <K extends keyof AdhererFormState>(
    field: K,
    value: AdhererFormState[K],
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
    setAlertMessage("");
    setErrors((current) => {
      if (!current[field as string]) return current;
      const next = { ...current };
      delete next[field as string];
      return next;
    });
  };

  const updateOemContact = (index: number, field: keyof OemContact, value: string) => {
    setForm((current) => ({
      ...current,
      oemContacts: current.oemContacts.map((contact, contactIndex) =>
        contactIndex === index ? { ...contact, [field]: value } : contact,
      ),
    }));
  };

  const toggleCertification = (certification: string) => {
    setForm((current) => {
      const exists = current.certifications.includes(certification);
      return {
        ...current,
        certifications: exists
          ? current.certifications.filter((item) => item !== certification)
          : [...current.certifications, certification],
      };
    });
  };

  const addOemContact = () => {
    setForm((current) => ({
      ...current,
      oemContacts: [...current.oemContacts, { ...initialOemContact }],
    }));
  };

  const scrollToWizard = () => {
    document.getElementById("aida-wizard")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const scrollToAlert = () => {
    alertRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  const goToStep = (step: number) => {
    setCurrentStep(step);
    setErrors({});
    setTimeout(scrollToWizard, 0);
  };

  const validateStep = (step: number) => {
    const nextErrors: Record<string, string> = {};

    if (step === 0) {
      if (!form.companyName.trim()) nextErrors.companyName = "Nom de la societe obligatoire.";
      if (!form.companyType) nextErrors.companyType = "Selectionnez le type de compte.";
      if (!form.companyProfileType) {
        nextErrors.companyProfileType = "Selectionnez le type de societe.";
      }
      if (!form.siteName.trim()) nextErrors.siteName = "Unite, site ou usine obligatoire.";
      if (!form.wilaya) nextErrors.wilaya = "Selectionnez la wilaya.";
      if (!form.factoryAddress.trim()) nextErrors.factoryAddress = "Emplacement de l'usine obligatoire.";
      if (!form.phone.trim()) nextErrors.phone = "Telephone obligatoire.";
      if (!form.email.trim()) {
        nextErrors.email = "Email obligatoire.";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
        nextErrors.email = "Email non valide.";
      }
    }

    return nextErrors;
  };

  const collectAllErrors = () => {
    const allErrors: Record<string, string> = {};

    for (let step = 0; step < steps.length; step += 1) {
      Object.assign(allErrors, validateStep(step));
    }

    return allErrors;
  };

  const goNext = () => {
    const stepErrors = validateStep(currentStep);

    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      setAlertType("danger");
      setAlertMessage("Veuillez corriger les champs marques en rouge avant de continuer.");
      toast.error("Veuillez completer les champs obligatoires.");
      setTimeout(scrollToAlert, 0);
      return;
    }

    setAlertMessage("");
    setErrors({});
    goToStep(Math.min(currentStep + 1, steps.length - 1));
  };

  const validateAll = () => {
    for (let step = 0; step < steps.length; step += 1) {
      const stepErrors = validateStep(step);
      if (Object.keys(stepErrors).length > 0) {
        setCurrentStep(step);
        setErrors(stepErrors);
        setTimeout(scrollToWizard, 0);
        return false;
      }
    }

    setErrors({});
    return true;
  };

  const allValidationErrors = collectAllErrors();
  const missingFieldsCount = Object.keys(allValidationErrors).length;
  const formReady = missingFieldsCount === 0;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAlertMessage("");

    if (!validateAll()) {
      setAlertType("danger");
      setAlertMessage(
        "Formulaire incomplet : veuillez corriger les champs obligatoires marques en rouge avant l'envoi.",
      );
      toast.error("Formulaire incomplet. Corrigez les champs obligatoires.");
      setTimeout(scrollToAlert, 0);
      return;
    }

    setLoading(true);

    const payload = {
      ...form,
      productsCount: String(productCount || form.productsCount),
      factoryLocation: `${form.wilaya} - ${form.factoryAddress}`,
      respondentName: "",
      responseDate: new Date().toISOString().slice(0, 10),
      signature: "",
    };

    try {
      const response = await fetch(`${backendUrl}/api/adherer`, {
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
        method: "POST",
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(getAdhesionSubmitError(response.status));
      }

      setAlertType("success");
      setAlertMessage(
        data.username
          ? `Fiche envoyee a AIDA. Compte cree : ${data.username} / mot de passe : ${
              data.default_password || "123456"
            }.`
          : "Fiche envoyee a AIDA avec succes.",
      );
      toast.success("Votre demande a ete envoyee a AIDA avec succes.");
      setTimeout(scrollToAlert, 0);
    } catch (error: any) {
      setAlertType("danger");
      const message =
        error instanceof TypeError
          ? "Le service est momentanement indisponible. Veuillez reessayer plus tard ou contacter AIDA."
          : error.message || getAdhesionSubmitError(500);
      setAlertMessage(message);
      toast.error(message);
      setTimeout(scrollToAlert, 0);
    } finally {
      setLoading(false);
    }
  };

  const renderSectionTitle = (
    number: number,
    title: string,
    icon: React.ReactNode,
    help?: string,
  ) => (
    <div className="aida-section-title mb-3">
      <span className="aida-number">{number}</span>
      <span className="aida-icon">{icon}</span>
      <div>
        <h2 className="h5 fw-bold mb-0">{title}</h2>
        {help && <p className="text-muted mb-0">{help}</p>}
      </div>
    </div>
  );

  return (
    <div className="aida-adherer-page">
      <style>
        {`
          .aida-adherer-page {
            background: #f5f6f8;
            color: #172033;
            min-height: 100vh;
          }

          .aida-shell {
            max-width: 1120px;
          }

          .aida-header,
          .aida-section,
          .aida-stepper {
            background: #fff;
            border: 1px solid #e3e7ec;
            border-radius: 8px;
            box-shadow: 0 14px 36px rgba(15, 23, 42, 0.06);
          }

          .aida-logo {
            max-height: 86px;
            max-width: 150px;
            object-fit: contain;
          }

          .aida-top-rule {
            background: #b88420;
            height: 4px;
          }

          .aida-section-title {
            align-items: center;
            border-bottom: 1px solid #edf0f3;
            display: flex;
            gap: 10px;
            padding-bottom: 14px;
          }

          .aida-number {
            align-items: center;
            background: #b88420;
            border-radius: 4px;
            color: #fff;
            display: inline-flex;
            font-weight: 800;
            height: 30px;
            justify-content: center;
            min-width: 30px;
          }

          .aida-icon {
            align-items: center;
            background: #fff7df;
            border: 1px solid #efd390;
            border-radius: 6px;
            color: #9b6b10;
            display: inline-flex;
            font-size: 19px;
            height: 38px;
            justify-content: center;
            min-width: 38px;
          }

          .aida-step-button {
            background: transparent;
            border: 0;
            color: #6b7280;
            min-height: 54px;
            text-align: center;
          }

          .aida-step-button.active {
            color: #111827;
            font-weight: 800;
          }

          .aida-step-index {
            align-items: center;
            background: #eef1f5;
            border-radius: 50%;
            display: inline-flex;
            height: 28px;
            justify-content: center;
            margin-right: 8px;
            width: 28px;
          }

          .aida-step-button.active .aida-step-index,
          .aida-step-button.done .aida-step-index {
            background: #b88420;
            color: #fff;
          }

          .aida-segment {
            align-items: center;
            border: 1px solid #d8dee6;
            border-radius: 8px;
            cursor: pointer;
            display: flex;
            gap: 12px;
            justify-content: flex-start;
            min-height: 62px;
            overflow: hidden;
            padding: 16px;
            transition: border-color .15s, box-shadow .15s;
          }

          .aida-segment.active {
            border-color: #b88420;
            box-shadow: 0 0 0 3px rgba(184, 132, 32, 0.15);
          }

          .aida-segment .form-check-input {
            float: none;
            flex: 0 0 auto;
            height: 18px;
            margin: 0;
            position: static;
            width: 18px;
          }

          .aida-segment-label {
            display: block;
            font-weight: 800;
            line-height: 1.2;
            min-width: 0;
            overflow-wrap: anywhere;
            padding-left: 0;
          }

          .aida-field-card {
            background: #fbfcfd;
            border: 1px solid #edf0f3;
            border-radius: 8px;
            padding: 14px;
          }

          .aida-field-icon {
            background: #fff7df;
            border-color: #e2cf9c;
            color: #9b6b10;
            min-width: 44px;
          }

          .aida-field-card .input-group-text {
            align-items: center;
            display: flex;
            justify-content: center;
          }

          .aida-field-card .input-group-text i {
            font-size: 18px;
            line-height: 1;
          }

          .aida-field-card .invalid-feedback {
            display: block;
          }

          .aida-field-card textarea {
            min-height: 104px;
          }

          .aida-progress {
            height: 10px;
          }

          .aida-summary-box {
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            min-height: 86px;
          }

          .aida-submit-alert {
            border-radius: 8px;
            font-weight: 700;
          }

          .aida-error-list {
            padding-left: 18px;
          }

          .aida-error-list li + li {
            margin-top: 4px;
          }

          .form-control,
          .form-select,
          .input-group-text {
            border-radius: 6px;
          }

          .form-label {
            color: #2f3848;
          }

          @media (max-width: 767px) {
            .aida-header,
            .aida-section,
            .aida-stepper {
              border-radius: 0;
              margin-left: -12px;
              margin-right: -12px;
            }

            .aida-logo {
              max-height: 72px;
            }

            .aida-step-button {
              font-size: 13px;
              min-height: 46px;
            }
          }
        `}
      </style>

      <div className="container-fluid aida-shell py-4 py-lg-5">
        <header className="aida-header overflow-hidden mb-4">
          <div className="aida-top-rule" />
          <div className="p-3 p-md-4">
            <div className="row g-3 align-items-center">
              <div className="col-md-auto text-center text-md-start">
                <img alt="AIDA" className="aida-logo" src="/asset/images/logo.png" />
              </div>
              <div className="col-md">
                <h1 className="h3 fw-bold mb-1">QUESTIONNAIRE D'AVANCEMENT DES PROJETS D'INTEGRATION</h1>
                <p className="fw-semibold mb-1">Commission Nationale de Sous-Traitance Automobile du CREA</p>
                <p className="text-muted mb-0">
                  Ce document vise a accompagner les sous-traitants dans leurs projets
                  d'integration et a identifier les blocages a lever.
                </p>
              </div>
              <div className="col-md-auto text-center text-md-end">
                <div className="d-flex justify-content-center gap-3 fs-2 text-dark">
                  <IoCarSportOutline title="Automobile" />
                  <IoPeopleOutline title="AIDA" />
                  <IoFlagOutline title="Industrie" />
                </div>
              </div>
            </div>
          </div>
        </header>

        <form id="aida-wizard" onSubmit={handleSubmit}>
          <div className="aida-stepper p-2 mb-4">
            <div className="row g-1">
              {steps.map((step, index) => (
                <div className="col-6 col-md" key={step}>
                  <button
                    className={`aida-step-button w-100 px-2 py-2 ${
                      currentStep === index ? "active" : ""
                    } ${currentStep > index ? "done" : ""}`}
                    onClick={() => goToStep(index)}
                    type="button"
                  >
                    <span className="aida-step-index">{index + 1}</span>
                    <span>{step}</span>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {alertMessage && (
            <div
              className={`alert alert-${alertType} mb-4 aida-submit-alert`}
              ref={alertRef}
              role="alert"
            >
              {alertMessage}
            </div>
          )}

          {Object.keys(errors).length > 0 && (
            <div className="alert alert-danger d-flex gap-2 mb-4" role="alert">
              <IoAlertCircleOutline className="fs-4 flex-shrink-0" />
              <div>
                <strong>Formulaire incomplet.</strong>
                <div>Veuillez corriger les champs marques en rouge.</div>
                <ul className="aida-error-list mb-0 mt-2">
                  {Object.values(errors).map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {currentStep === 0 && (
            <section className="aida-section p-3 p-md-4 mb-4">
              {renderSectionTitle(1, "Identification de l'entreprise", <IoBusinessOutline />)}

              <div className="row g-3">
                <div className="col-lg-4">
                  <div className="aida-field-card h-100">
                    <label className="form-label fw-semibold">Nom de la societe <span className="text-danger">*</span></label>
                    <div className="input-group">
                      <span className="input-group-text aida-field-icon">
                        <i className="ri-building-2-line" />
                      </span>
                      <input
                        className={inputClass(errors, "companyName")}
                        onChange={(event) => updateForm("companyName", event.target.value)}
                        placeholder="Raison sociale de l'entreprise"
                        value={form.companyName}
                      />
                    </div>
                    <FieldError errors={errors} field="companyName" />
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="aida-field-card h-100">
                    <label className="form-label fw-semibold">Type de compte <span className="text-danger">*</span></label>
                    <div className="input-group">
                      <span className="input-group-text aida-field-icon">
                        <i className="ri-briefcase-4-line" />
                      </span>
                      <select
                        className={selectClass(errors, "companyType")}
                        onChange={(event) => updateForm("companyType", event.target.value as CompanyType)}
                        value={form.companyType}
                      >
                        <option value="">Selectionner</option>
                        <option value="OEM">OEM / Constructeur</option>
                        <option value="Supplier">Supplier / Fournisseur</option>
                        <option value="Expert">Expert</option>
                      </select>
                    </div>
                    <FieldError errors={errors} field="companyType" />
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="aida-field-card h-100">
                    <label className="form-label fw-semibold">Type de societe <span className="text-danger">*</span></label>
                    <div className="input-group">
                      <span className="input-group-text aida-field-icon">
                        <i className="ri-price-tag-3-line" />
                      </span>
                      <select
                        className={selectClass(errors, "companyProfileType")}
                        onChange={(event) =>
                          updateForm(
                            "companyProfileType",
                            event.target.value as CompanyProfileType,
                          )
                        }
                        value={form.companyProfileType}
                      >
                        <option value="">Selectionner</option>
                        {companyProfileTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <FieldError errors={errors} field="companyProfileType" />
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="aida-field-card h-100">
                    <label className="form-label fw-semibold">Unite / Site / Usine <span className="text-danger">*</span></label>
                    <div className="input-group">
                      <span className="input-group-text aida-field-icon">
                        <i className="ri-factory-line" />
                      </span>
                      <input
                        className={inputClass(errors, "siteName")}
                        onChange={(event) => updateForm("siteName", event.target.value)}
                        placeholder="Nom de l'unite ou du site"
                        value={form.siteName}
                      />
                    </div>
                    <FieldError errors={errors} field="siteName" />
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="aida-field-card h-100">
                    <label className="form-label fw-semibold">Wilaya <span className="text-danger">*</span></label>
                    <div className="input-group">
                      <span className="input-group-text aida-field-icon">
                        <i className="ri-map-pin-line" />
                      </span>
                      <select
                        className={selectClass(errors, "wilaya")}
                        onChange={(event) => updateForm("wilaya", event.target.value)}
                        value={form.wilaya}
                      >
                        <option value="">Selectionner une wilaya</option>
                        {wilayas.map((wilaya) => (
                          <option key={wilaya} value={wilaya}>
                            {wilaya}
                          </option>
                        ))}
                      </select>
                    </div>
                    <FieldError errors={errors} field="wilaya" />
                  </div>
                </div>
                <div className="col-lg-12">
                  <div className="aida-field-card">
                    <label className="form-label fw-semibold">Emplacement de l'usine <span className="text-danger">*</span></label>
                    <div className="input-group">
                      <span className="input-group-text aida-field-icon">
                        <i className="ri-road-map-line" />
                      </span>
                      <input
                        className={inputClass(errors, "factoryAddress")}
                        onChange={(event) => updateForm("factoryAddress", event.target.value)}
                        placeholder="Adresse complete, zone industrielle, commune..."
                        value={form.factoryAddress}
                      />
                    </div>
                    <FieldError errors={errors} field="factoryAddress" />
                  </div>
                </div>
                <div className="col-lg-8">
                  <div className="aida-field-card h-100">
                    <label className="form-label fw-semibold">Produit(s) fabrique(s)</label>
                    <div className="input-group">
                      <span className="input-group-text aida-field-icon align-items-start pt-3">
                        <i className="ri-box-3-line" />
                      </span>
                      <textarea
                        className={inputClass(errors, "products")}
                        onChange={(event) => updateForm("products", event.target.value)}
                        placeholder="Ex. pieces plastique, faisceaux, emboutissage..."
                        rows={3}
                        value={form.products}
                      />
                    </div>
                    <FieldError errors={errors} field="products" />
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="aida-field-card h-100">
                    <label className="form-label fw-semibold">Nombre de produits</label>
                    <div className="input-group">
                      <span className="input-group-text aida-field-icon">
                        <i className="ri-list-ordered" />
                      </span>
                      <input
                        className={inputClass(errors, "productsCount")}
                        min="0"
                        onChange={(event) => updateForm("productsCount", event.target.value)}
                        placeholder="0"
                        type="number"
                        value={form.productsCount}
                      />
                    </div>
                    <FieldError errors={errors} field="productsCount" />
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="aida-field-card h-100">
                    <label className="form-label fw-semibold">Capacite theorique de l'usine</label>
                    <div className="input-group">
                      <span className="input-group-text aida-field-icon">
                        <i className="ri-dashboard-3-line" />
                      </span>
                      <input
                        className={inputClass(errors, "factoryCapacity")}
                        onChange={(event) => updateForm("factoryCapacity", event.target.value)}
                        placeholder="Ex. 20 000 pieces / an"
                        value={form.factoryCapacity}
                      />
                    </div>
                    <FieldError errors={errors} field="factoryCapacity" />
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="aida-field-card h-100">
                    <label className="form-label fw-semibold">Responsable / contact</label>
                    <div className="input-group">
                      <span className="input-group-text aida-field-icon">
                        <i className="ri-user-3-line" />
                      </span>
                      <input
                        className={inputClass(errors, "contactName")}
                        onChange={(event) => updateForm("contactName", event.target.value)}
                        placeholder="Nom et fonction"
                        value={form.contactName}
                      />
                    </div>
                    <FieldError errors={errors} field="contactName" />
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="aida-field-card h-100">
                    <label className="form-label fw-semibold">Telephone <span className="text-danger">*</span></label>
                    <div className="input-group">
                      <span className="input-group-text aida-field-icon">
                        <i className="ri-phone-line" />
                      </span>
                      <input
                        className={inputClass(errors, "phone")}
                        onChange={(event) => updateForm("phone", event.target.value)}
                        placeholder="0555 12 34 56"
                        type="tel"
                        value={form.phone}
                      />
                    </div>
                    <FieldError errors={errors} field="phone" />
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="aida-field-card h-100">
                    <label className="form-label fw-semibold">Email <span className="text-danger">*</span></label>
                    <div className="input-group">
                      <span className="input-group-text aida-field-icon">
                        <i className="ri-mail-line" />
                      </span>
                      <input
                        className={inputClass(errors, "email")}
                        onChange={(event) => updateForm("email", event.target.value)}
                        placeholder="contact@societe.dz"
                        type="email"
                        value={form.email}
                      />
                    </div>
                    <FieldError errors={errors} field="email" />
                  </div>
                </div>
              </div>
            </section>
          )}

          {currentStep === 1 && (
            <section className="aida-section p-3 p-md-4 mb-4">
              {renderSectionTitle(
                2,
                "Certifications et homologation",
                <IoInformationCircleOutline />,
                "Indiquez les certifications disponibles et l'etat d'homologation des produits.",
              )}

              <div className="row g-3">
                <div className="col-lg-6">
                  <div className="aida-field-card h-100">
                    <label className="form-label fw-semibold d-block">
                      Certifications disponibles
                    </label>
                    <div className="row g-2">
                      {certificationOptions.map((certification) => {
                        const selected = form.certifications.includes(certification);
                        return (
                          <div className="col-sm-6" key={certification}>
                            <label className={`aida-segment ${selected ? "active" : ""}`}>
                              <input
                                checked={selected}
                                className="form-check-input"
                                onChange={() => toggleCertification(certification)}
                                type="checkbox"
                              />
                              <span className="aida-segment-label">{certification}</span>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="aida-field-card h-100">
                    <label className="form-label fw-semibold d-block">
                      Les produits sont-ils homologues ?
                    </label>
                    <div className="d-flex flex-wrap gap-3">
                      {(["Oui", "Non", "En cours", "Non renseigne"] as HomologationAnswer[]).map((value) => (
                        <label className="form-check d-flex align-items-center gap-2 mb-0" key={value}>
                          <input
                            checked={form.productHomologated === value}
                            className="form-check-input"
                            onChange={() => updateForm("productHomologated", value)}
                            type="radio"
                          />
                          <span className="fw-semibold">{value}</span>
                        </label>
                      ))}
                    </div>
                    {errors.productHomologated && (
                      <div className="text-danger small mt-2">{errors.productHomologated}</div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}

          {currentStep === 2 && (
            <section className="aida-section p-3 p-md-4 mb-4">
              {renderSectionTitle(3, "Relation avec les constructeurs / OEM", <IoPeopleOutline />)}

              <div className="mb-3">
                <label className="form-label fw-semibold d-block">
                  Avez-vous ete contacte par un constructeur / OEM ?
                </label>
                <div className="d-flex flex-wrap gap-3">
                  {(["Oui", "Non", "Non renseigne"] as YesNo[]).map((value) => (
                    <label className="form-check d-flex align-items-center gap-2 mb-0" key={value}>
                      <input
                        checked={form.contactedByOem === value}
                        className="form-check-input"
                        onChange={() => updateForm("contactedByOem", value)}
                        type="radio"
                      />
                      <span className="fw-semibold">{value}</span>
                    </label>
                  ))}
                </div>
                {errors.contactedByOem && (
                  <div className="text-danger small mt-2">{errors.contactedByOem}</div>
                )}
              </div>

              <div className="table-responsive">
                <table className="table table-bordered align-middle mb-2">
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: 180 }}>Constructeur / OEM</th>
                      <th style={{ minWidth: 210 }}>Nom du contact / fonction</th>
                      <th style={{ minWidth: 150 }}>Telephone</th>
                      <th style={{ minWidth: 180 }}>Depuis combien de mois ?</th>
                      <th style={{ minWidth: 220 }}>Statut / niveau d'avancement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.oemContacts.map((contact, index) => (
                      <tr key={`oem-contact-${index}`}>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            onChange={(event) =>
                              updateOemContact(index, "constructorName", event.target.value)
                            }
                            value={contact.constructorName}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            onChange={(event) =>
                              updateOemContact(index, "contactName", event.target.value)
                            }
                            value={contact.contactName}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            onChange={(event) => updateOemContact(index, "phone", event.target.value)}
                            value={contact.phone}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            onChange={(event) =>
                              updateOemContact(index, "duration", event.target.value)
                            }
                            value={contact.duration}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            onChange={(event) => updateOemContact(index, "status", event.target.value)}
                            value={contact.status}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button className="btn btn-outline-secondary btn-sm" onClick={addOemContact} type="button">
                Ajouter une ligne
              </button>
            </section>
          )}

          {currentStep === 3 && (
            <section className="aida-section p-3 p-md-4 mb-4">
              {renderSectionTitle(
                4,
                "Problemes / blocages rencontres",
                <IoAlertCircleOutline />,
                "Precisez les difficultes rencontrees : absence de reponse, attente depuis 8, 9, 14 mois, problemes techniques, qualite, prix, homologation, financement, logistique, matiere premiere, etc.",
              )}

              <textarea
                className={inputClass(errors, "blockers")}
                onChange={(event) => updateForm("blockers", event.target.value)}
                rows={7}
                value={form.blockers}
              />
              <FieldError errors={errors} field="blockers" />
            </section>
          )}

          {currentStep === 4 && (
            <section className="aida-section p-3 p-md-4 mb-4">
              {renderSectionTitle(5, "Besoins d'accompagnement / action attendue", <IoDocumentTextOutline />)}

              <textarea
                className={inputClass(errors, "supportNeeds")}
                onChange={(event) => updateForm("supportNeeds", event.target.value)}
                rows={5}
                value={form.supportNeeds}
              />
              <FieldError errors={errors} field="supportNeeds" />

              <div className="mt-4">
                <div className="d-flex justify-content-between mb-1">
                  <span className="fw-semibold">Votre fiche est completee a {completedFields} %</span>
                  <span className="text-muted">{completedFields}/100</span>
                </div>
                <div className="progress aida-progress">
                  <div
                    aria-valuemax={100}
                    aria-valuemin={0}
                    aria-valuenow={completedFields}
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: `${completedFields}%`, background: "#b88420" }}
                  />
                </div>
              </div>

              <div className="row g-3 mt-1">
                <div className="col-6 col-lg-3">
                  <div className="aida-summary-box p-3">
                    <span className="d-block text-muted">Produits</span>
                    <strong className="h4 mb-0">{productCount}</strong>
                  </div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="aida-summary-box p-3">
                    <span className="d-block text-muted">Certifications</span>
                    <strong className="h4 mb-0">{form.certifications.length}</strong>
                  </div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="aida-summary-box p-3">
                    <span className="d-block text-muted">Relations OEM</span>
                    <strong className="h4 mb-0">
                      {
                        form.oemContacts.filter((contact) =>
                          Object.values(contact).some((value) => value.trim()),
                        ).length
                      }
                    </strong>
                  </div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="aida-summary-box p-3">
                    <span className="d-block text-muted">Compte</span>
                    <strong className="d-block text-truncate">
                      {form.companyType || "A definir"}
                    </strong>
                  </div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="aida-summary-box p-3">
                    <span className="d-block text-muted">Type societe</span>
                    <strong className="d-block text-truncate">
                      {getCompanyProfileTypeLabel(form.companyProfileType)}
                    </strong>
                  </div>
                </div>
              </div>
            </section>
          )}

          <div className="aida-section p-3 p-md-4">
            <div className="d-flex flex-column flex-md-row gap-2 justify-content-end">
              <div className="d-flex flex-column flex-sm-row gap-2 justify-content-end">
                {!formReady && currentStep === steps.length - 1 && (
                  <div className="alert alert-warning py-2 px-3 mb-0">
                    {missingFieldsCount} champ(s) obligatoire(s) restent a completer.
                  </div>
                )}

                <button
                  className="btn btn-outline-dark"
                  disabled={currentStep === 0}
                  onClick={() => goToStep(Math.max(currentStep - 1, 0))}
                  type="button"
                >
                  Precedent
                </button>

                {currentStep < steps.length - 1 ? (
                  <button className="btn btn-warning fw-bold" onClick={goNext} type="button">
                    Suivant
                  </button>
                ) : (
                  <button
                    className="btn btn-warning fw-bold"
                    disabled={loading}
                    title={!formReady ? "Completez les champs obligatoires avant l'envoi" : "Envoyer a AIDA"}
                    type="submit"
                  >
                    {loading ? (
                      <BeatLoader color="#111827" loading={loading} size={8} />
                    ) : (
                      <>
                        <IoCloudUploadOutline style={{ marginRight: 6 }} />
                        Envoyer a AIDA
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Adherer;
